/**
 * Resolve human-readable queue labels from org unit ids.
 */

import type { OrgUnit } from '../types/prioritization/enterpriseIdentity';

const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

export function isLikelyUuid(value: string): boolean {
  return UUID_PREFIX.test(value);
}

export type QueueNameOption = { id: string; name: string };

/**
 * Returns a display label for a queue org unit id, or undefined when unknown.
 */
export function resolveQueueLabel(
  queueId: string | null | undefined,
  orgUnits: Map<string, OrgUnit>,
  queueOptions?: ReadonlyArray<QueueNameOption>,
): string | undefined {
  if (!queueId) return undefined;

  const fromOptions = queueOptions?.find((q) => q.id === queueId)?.name;
  if (fromOptions) return fromOptions;

  const ou = orgUnits.get(queueId);
  if (ou) {
    const label = ou.displayName?.trim() || ou.name?.trim();
    if (label) return label;
  }

  return undefined;
}

/**
 * Label for UI: never falls back to raw UUIDs.
 */
export function resolveQueueDisplayLabel(
  queueId: string | null | undefined,
  orgUnits: Map<string, OrgUnit>,
  queueOptions?: ReadonlyArray<QueueNameOption>,
  fallback = 'General',
): string {
  const label = resolveQueueLabel(queueId, orgUnits, queueOptions);
  if (label) return label;
  if (!queueId) return fallback;
  return fallback;
}

/**
 * Prefer a resolved label; omit values that are only a UUID (stale prop).
 */
export function coalesceQueueLabel(
  queueLabel: string | undefined,
  queueId: string | null | undefined,
  orgUnits: Map<string, OrgUnit>,
  queueOptions?: ReadonlyArray<QueueNameOption>,
): string | undefined {
  const resolved = resolveQueueLabel(queueId, orgUnits, queueOptions);
  if (resolved) return resolved;
  if (queueLabel && !isLikelyUuid(queueLabel)) return queueLabel;
  return undefined;
}
